import { useCallback, useEffect, useState } from 'react';
import { EMAIL_EVENTS, EmailActivity, EmailDateRange, EmailEvent, getEmailActivity } from 'services';
import * as XLSX from 'xlsx';
import { EVENT_COLORS, EVENT_LABELS, EXPORT_LIMIT, PAGE_SIZE } from './constants';

interface Props {
  range: EmailDateRange;
}

const formatDateTime = (value: string) => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
};

export default function EmailActivityTable({ range }: Props) {
  // O termo digitado só vira filtro ao confirmar, para não consultar a cada tecla.
  const [recipientInput, setRecipientInput] = useState('');
  const [filters, setFilters] = useState<{ event: EmailEvent | null; recipient: string }>({
    event: null,
    recipient: '',
  });
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<EmailActivity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Trocar o período invalida a página corrente. O ajuste é feito durante a
  // renderização — e não num efeito — para que a busca abaixo já enxergue a
  // página 1 e não dispare uma requisição descartável com a página antiga.
  const [appliedRange, setAppliedRange] = useState(range);
  if (range !== appliedRange) {
    setAppliedRange(range);
    setPage(1);
  }

  // A busca é disparada pela mudança de estado, e não de dentro do onClick: assim
  // a requisição sempre usa a página e os filtros já aplicados, sem stale closure.
  useEffect(() => {
    let active = true;
    setLoading(true);

    getEmailActivity({ ...range, ...filters }, page, PAGE_SIZE)
      .then(result => {
        if (!active) return;
        setRows(result.rows);
        setTotal(result.total);
      })
      .catch(error => console.log(error))
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [range, filters, page]);

  const applyFilters = (event: EmailEvent | null, recipient: string) => {
    setFilters({ event, recipient });
    setPage(1);
  };

  const paginate = (direction: number) => {
    const next = page + direction;
    if (next >= 1 && next <= totalPages) setPage(next);
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const { rows: allRows } = await getEmailActivity({ ...range, ...filters }, 1, EXPORT_LIMIT);

      const worksheet = XLSX.utils.json_to_sheet(
        allRows.map(row => ({
          Data: formatDateTime(row.event_at),
          Destinatário: row.recipient,
          Assunto: row.subject,
          Evento: EVENT_LABELS[row.event] || row.event,
          Status: row.status,
        })),
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'E-mails');
      XLSX.writeFile(workbook, 'relatorio_emails.xlsx');
    } catch (error) {
      console.log(error);
    } finally {
      setExporting(false);
    }
  }, [range, filters]);

  return (
    <div className="bg-white p-4 rounded shadow">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
        <h4 className="text-lg font-bold">Eventos ({total.toLocaleString('pt-BR')})</h4>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-sm font-medium mb-1">Evento</label>
            <select
              value={filters.event ?? ''}
              onChange={e => applyFilters((e.target.value || null) as EmailEvent | null, filters.recipient)}
              className="p-2 border rounded"
            >
              <option value="">Todos os eventos</option>
              {EMAIL_EVENTS.map(event => (
                <option key={event} value={event}>
                  {EVENT_LABELS[event]}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Destinatário</label>
            <input
              type="text"
              placeholder="Filtrar por destinatário"
              value={recipientInput}
              onChange={e => setRecipientInput(e.target.value)}
              onKeyUp={e => e.key === 'Enter' && applyFilters(filters.event, recipientInput)}
              className="p-2 border rounded"
            />
          </div>

          <button
            onClick={() => applyFilters(filters.event, recipientInput)}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Buscar
          </button>

          <button
            onClick={handleExport}
            disabled={exporting || total === 0}
            className="bg-green-500 text-white px-4 py-2 rounded disabled:opacity-50"
          >
            {exporting ? 'Exportando...' : 'Exportar Excel'}
          </button>
        </div>
      </div>

      {total > EXPORT_LIMIT && (
        <p className="text-sm text-gray-500 mb-2">
          A exportação leva as {EXPORT_LIMIT.toLocaleString('pt-BR')} linhas mais recentes do período.
        </p>
      )}

      <div className="overflow-auto max-h-[500px]">
        <table className="w-full table-auto">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">DATA</th>
              <th className="p-2 text-left">DESTINATÁRIO</th>
              <th className="p-2 text-left">ASSUNTO</th>
              <th className="p-2 text-left">EVENTO</th>
              <th className="p-2 text-left">STATUS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row._id} className="border-t">
                <td className="p-2 whitespace-nowrap">{formatDateTime(row.event_at)}</td>
                <td className="p-2">{row.recipient}</td>
                <td className="p-2">{row.subject}</td>
                <td className="p-2">
                  <span
                    className="text-white text-xs px-2 py-1 rounded whitespace-nowrap"
                    style={{ backgroundColor: EVENT_COLORS[row.event] ?? '#777' }}
                  >
                    {EVENT_LABELS[row.event] || row.event}
                  </span>
                </td>
                <td className="p-2">{row.status}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && <p className="text-center text-gray-500 m-4">Carregando...</p>}
        {!loading && rows.length === 0 && <p className="text-center text-gray-500 m-4">Nenhum evento no período selecionado.</p>}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-4 mt-4">
          <button onClick={() => paginate(-1)} className="px-2 py-1 border rounded" disabled={page <= 1}>
            ‹
          </button>
          <span>
            Página {page} de {totalPages}
          </span>
          <button onClick={() => paginate(1)} className="px-2 py-1 border rounded" disabled={page >= totalPages}>
            ›
          </button>
        </div>
      )}
    </div>
  );
}
